import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, TextField, Typography, Alert, Paper, useTheme } from "@mui/material";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import api from "../../api/client";

/**
 * Placeholder for migrating your HTML/CSS canvas creative editor into React.
 * Layout JSON is persisted on the server under each brand's layouts folder.
 */
export default function LayoutEditor() {
  const { brandId, layoutId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isNew = !layoutId;

  const [title, setTitle] = useState("New layout");
  const [fileName, setFileName] = useState(`layout-${Date.now()}.json`);
  const [documentJson, setDocumentJson] = useState(
    JSON.stringify({ version: 1, elements: [], note: "Replace with editor export" }, null, 2)
  );
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!layoutId) return;
    (async () => {
      try {
        const { data } = await api.get(`/api/brands/${brandId}/layouts/${layoutId}`);
        setTitle(data.title || "");
        setFileName(data.fileName || "layout.json");
        setDocumentJson(JSON.stringify(data.document ?? {}, null, 2));
      } catch (e) {
        setMessage(e.response?.data?.message || "Failed to load layout");
      }
    })();
  }, [brandId, layoutId]);

  const parseDoc = () => {
    try {
      return JSON.parse(documentJson || "{}");
    } catch {
      throw new Error("Invalid JSON in layout document");
    }
  };

  const save = async (overwrite = false) => {
    setMessage("");
    try {
      const document = parseDoc();
      if (isNew) {
        const res = await api.post(
          `/api/brands/${brandId}/layouts`,
          { title, fileName, document },
          { params: { overwrite } }
        );
        const data = res.data;
        setMessage(`Saved as ${data.fileName}`);
        navigate(`/brands/${brandId}/editor/${data.id}`, { replace: true });
      } else {
        await api.patch(`/api/brands/${brandId}/layouts/${layoutId}`, { title, document });
        setMessage("Layout updated.");
      }
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.requiresOverwrite) {
        const ok = window.confirm("A layout with this file name exists. Overwrite?");
        if (ok) await save(true);
        return;
      }
      setMessage(e.response?.data?.message || e.message || "Save failed");
    }
  };

  return (
    <Box m="20px">
      <Header
        title="LAYOUT EDITOR"
        subtitle={
          isNew
            ? "Scaffold — connect your migrated canvas tools here and bind to document JSON."
            : "Edit layout document (server-backed)"
        }
      />
      {message ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {message}
        </Alert>
      ) : null}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: colors.primary[400] }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <TextField
            label="File name"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            disabled={!isNew}
            helperText={isNew ? "Must end with .json" : "Rename via duplicate if needed"}
          />
        </Box>
      </Paper>
      <TextField
        label="Layout document (JSON)"
        value={documentJson}
        onChange={(e) => setDocumentJson(e.target.value)}
        fullWidth
        multiline
        minRows={16}
        sx={{ mb: 2 }}
      />
      <Box display="flex" gap={2}>
        <Button variant="contained" color="secondary" onClick={() => save(false)}>
          Save layout
        </Button>
        <Button variant="outlined" onClick={() => navigate(`/brands/${brandId}/layouts`)}>
          Back to layouts
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" mt={2}>
        Export JPG/PNG/PDF and advanced canvas tools should be integrated in this component using your existing
        editor codebase.
      </Typography>
    </Box>
  );
}
